import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import invariant from 'tiny-invariant';
import { fileParts, supplementalDocumentParts } from '~/apollo/fragments';
import type {
  UploadStudySupplementalDocsRouteQuery,
  UploadStudySupplementalDocsRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { ExpandedIcon } from '~/components/common/icons/ExpandedIcon';
import { SuppDocList } from '~/components/upload/supportingObject/supplementalDocs/SuppDocList';
import { SuppDocUploader } from '~/components/upload/supportingObject/supplementalDocs/SuppDocUploader';
import { useUpdateStudyOutletContext } from '~/routes/upload/model/study/$studyId';

const UPLOAD_STUDY_SUPPLEMENTAL_DOCS_ROUTE = gql`
  query UploadStudySupplementalDocsRoute($studyId: Int!) {
    studyList(id: $studyId) {
      id
      supplementalDocuments {
        ...supplementalDocumentParts
        file {
          ...fileParts
        }
      }
    }
  }

  ${supplementalDocumentParts}
  ${fileParts}
`;

export default function UploadStudySupplementalDocumentsRoute() {
  const { study } = useUpdateStudyOutletContext();

  const { data, loading } = useQuery<
    UploadStudySupplementalDocsRouteQuery,
    UploadStudySupplementalDocsRouteQueryVariables
  >(UPLOAD_STUDY_SUPPLEMENTAL_DOCS_ROUTE, {
    variables: { studyId: study.id },
  });

  const refetchQueries: [
    PureQueryOptions<UploadStudySupplementalDocsRouteQueryVariables>,
  ] = [
    {
      query: UPLOAD_STUDY_SUPPLEMENTAL_DOCS_ROUTE,
      variables: { studyId: study.id },
    },
  ];

  const [uploaderVisible, setUploaderVisible] = useState(false);

  const docs = data?.studyList.find(
    s => s.id === study.id,
  )?.supplementalDocuments;

  if (loading) return <SpinnerPlaceholder />;
  invariant(docs, 'Error loading supplemental documents!');

  return (
    <div className="space-y-4">
      <Panel variant={uploaderVisible ? 'primary' : 'default'}>
        <Panel.Heading>
          <button
            type="button"
            onClick={() => setUploaderVisible(!uploaderVisible)}
            className="flex justify-between items-center gap-2 w-full"
          >
            <Panel.Title>Upload Supplemental Documents</Panel.Title>
            <ExpandedIcon expanded={uploaderVisible} />
          </button>
        </Panel.Heading>

        {uploaderVisible && (
          <Panel.Body>
            <SuppDocUploader
              studyId={study.id}
              refetchQueries={refetchQueries}
            />
          </Panel.Body>
        )}
      </Panel>

      <SuppDocList docs={docs} refetchQueries={refetchQueries} />
    </div>
  );
}
